import type { AuthInfo, AppInfo } from "@/types/loginApiDef"
import type { CheckLoginAccountInfo } from "@/types/accountApiDef"

import { defineStore } from "pinia"
import { ref, computed, watch } from 'vue'
import { usePageVisibilityStore } from "@/stores/pageVisibility"
import { usePageFocusObserver } from "@/stores/pageFocusObserver";
import { storeToRefs } from "pinia";
import { useContentsStore } from "@/stores/contents"
import * as loggers from "@/utils/loggers"
import { useSessionIdStore } from "@/stores/sessionId"
import { useItemKeysStore } from "@/stores/itemKeys"
import { useHeaderStore } from "@/stores/header"
import { useAppErrorStore } from "@/stores/appError"
import { useApiStore, apiIdOf, API_ORIGIN, axiosRetryx } from "@/stores/api"
import { OK, IP_ADDRESS_BLOCKING_CODE, SESSION_EXPIRED_CODE, apiDefaultDescriptions, type ApiOptions } from "@/stores/api"

export const useLoginStore = defineStore('login', () => {
  const { apiCall, checkResponseError, setXApiKey } = useApiStore()
  const headerStore = useHeaderStore()

  // ログインユーザー
  const accountInfo = ref<CheckLoginAccountInfo | null>(null)
  const currentAccount = computed(() => {
    return accountInfo.value as CheckLoginAccountInfo;
  })
  const updateCurrentAccount = (newAccountInfo: CheckLoginAccountInfo)=>{
    accountInfo.value = newAccountInfo
  }
  
  // アカウントログインチェックAPI
  const checkLoginAccount = async (
    args: {
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('checkLoginAccount');

    return await apiCall<CheckLoginAccountInfo>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/checkloginaccount";
      const data = {
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      // 501エラーの場合、ダイアログ表示ありに設定する
      if(response.data.code === SESSION_EXPIRED_CODE){
        apiOptions.noErrorDialog = false
      }

      // 401018エラーの場合、ダイアログ表示ありに設定する
      if(response.data.code === IP_ADDRESS_BLOCKING_CODE){
        apiOptions.noErrorDialog = false
      }
              
      if (checkResponseError(response, descriptions, apiOptions)) {
        accountInfo.value = null
        return;
      }
      const apiResult = response.data.account_info;
      accountInfo.value = apiResult

      // client_key_prefix を更新
      const { updateClientKeyPrefix } = useItemKeysStore()
      updateClientKeyPrefix(response.data.client_key_prefix)

      // トークお知らせURLをセット
      headerStore.talkNotificationUrls = {
        first: response.data.talk_notification_first_request_url,
        next: response.data.talk_notification_next_request_url,
      }
      return apiResult;
    })
  }

  const { pageVisible } = storeToRefs(usePageVisibilityStore());
  watch(
    () => pageVisible.value,
    (newPageVisible) => {
      if(newPageVisible){
        // 表示状態になったときのログインチェック
        checkLoginAccount({ __apiOptions: { noDisplayLoading: true } }); 
      }
    },
  )
  
  const { subscribe } = usePageFocusObserver();
  subscribe({
    onPageFocus() {
      // window focusで、ログインチェック
      checkLoginAccount({ __apiOptions: { noDisplayLoading: true } });
    },
  });

  // ログイン中のアプリ情報
  const loginAppInfoList = ref<Array<AppInfo>>([])
  const setLoginAppInfoList = (loginAppInfos: Array<AppInfo>) => {
    loginAppInfoList.value = loginAppInfos
  }

  // アカウントログインAPI 
  type LoginaccountResponse = (
    {
      loginResult: 'OK' | 'OTP_FAILED' | 'MFA_REQUIRED' | 'DUPLICATE_SESSION'
      isTfaEnabled: boolean
      showLicenseInput?: boolean
    } | {
      loginResult: 'MFA_REQUIRED_FIRST' 
      isTfaEnabled: boolean
      authInfo: AuthInfo
      showLicenseInput?: boolean
    }
  )
  const loginAccount = async (
    args: { 
      mail: string
      password: string
      onetime_password?: string
      app_type?: string
      app_name: string
      is_forced?: boolean,
      logout_session_id?: string,
      __apiOptions?: ApiOptions
    }
  ) => {
    const { updateAppError } = useAppErrorStore()
    const { loginCompleted } = useLoginStore()
    
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('loginAccount');

    return await apiCall<LoginaccountResponse>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/loginaccount";
      const data = {
        mail: args.mail,
        password: args.password,
        onetime_password: args.onetime_password,
        app_type: args.app_type,
        app_name: args.app_name,
        is_forced: args.is_forced,
        logout_session_id: args.logout_session_id,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig }).catch(e => {
        const httpStatus = (e.response ? e.response.status : undefined);
        if(httpStatus && httpStatus === 429){
          updateAppError({ tooManyRequestsError: true })
          return;
        }
        throw e;
      })
      if(!response) return;

      // 別端末でログイン済み（同時ログイン制限）
      const loginErrorCode = 401;
      const duplicateSessionErrorCode = 403;
      const mfaRequiredErrorCode = 201001;

      const checkSettings = (() => {
        if (args.onetime_password) {
          // ワンタイムパスワード指定の場合
          return {
            descriptions: {
              ...apiDefaultDescriptions,
              402: '',
            },
            okCodes: [OK, duplicateSessionErrorCode, loginErrorCode],
          }
        }
        // 通常のログイン
        return {
          descriptions: {
            ...apiDefaultDescriptions,
            402: '',
          },
          okCodes: [OK, loginErrorCode, duplicateSessionErrorCode, mfaRequiredErrorCode],
        }
      })()
      
      if (checkResponseError(response, checkSettings.descriptions, apiOptions, checkSettings.okCodes)) {
        return;
      }

      if (!args.onetime_password && response.data.code === loginErrorCode) {
        updateAppError({
          apiResponseCode: loginErrorCode,
          description: `ログインできませんでした
                        メールアドレスまたはパスワードが間違っています
                        登録内容をご確認の上、再度ご入力ください`,
          loginError: true,
          ...apiOptions,
        })
        return
      }

      const isTfaEnabled = response.data.is_tfa_enabled
      if(response.data.code === loginErrorCode){
        // ワンタイムパスワードエラー
        return { loginResult: 'OTP_FAILED', isTfaEnabled }
      }
      if(response.data.code === mfaRequiredErrorCode){
        // 多要素認証が必要
        if(response.data.auth_info) {
          return { loginResult: 'MFA_REQUIRED_FIRST', authInfo: response.data.auth_info, isTfaEnabled }
        }
        return { loginResult: 'MFA_REQUIRED', isTfaEnabled }
      }

      if(response.data.code === duplicateSessionErrorCode){
        // 別端末でログイン済み（同時ログイン制限）時のAPP情報
        loginAppInfoList.value = response.data.login_app_info_list
        return { loginResult: 'DUPLICATE_SESSION', isTfaEnabled }
      }

      // 工事ごとのコンテンツ保管場所のクリア
      await loginCompleted(response.data)

      // トークお知らせURLをセット
      headerStore.talkNotificationUrls = {
        first: response.data.talk_notification_first_request_url,
        next: response.data.talk_notification_next_request_url,
      }

      if(response.data.show_license_input){
        return { loginResult: 'OK', showLicenseInput: true, isTfaEnabled }
      }

      return { loginResult: 'OK', isTfaEnabled }
    })
  }
 
  // トークン有効期限確認API
  const checkTokenActive = async (
    args: { 
      onetime_token: string
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('checkTokenActive');

    return await apiCall<boolean>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/checktokenactive";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      const hasError = checkResponseError(response, descriptions, apiOptions);
      const activeToken = !hasError;
      // TODO どこも使用していない
      // context.commit('result/setActiveOnetimeToken', { onetimeToken: args.onetime_token, active: activeToken })
      return activeToken;
    })
  }

  // アカウント2要素認証設定登録API
  const registerAccountTfaSetting = async (
    args: { 
      mail: string
      password: string
      onetime_password: string
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('registerAccountTfaSetting');

    return await apiCall<'OK' | "REGISTER_NG">(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/registeraccounttfasetting";
      const data = {
        mail: args.mail,
        password: args.password,
        onetime_password: args.onetime_password,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      const otpErrorCode = 400027
      if (checkResponseError(response, descriptions, apiOptions, [OK, otpErrorCode])) {
        return;
      }
      if (response.data.code === otpErrorCode) {
        return "REGISTER_NG"
      }
      return 'OK';
    })
  }

  // アカウントログアウトAPI
  const logoutAccount = async (
    args: { 
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('logoutAccount');

    return await apiCall<'OK'>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/logoutaccount";
      const data = {
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      setXApiKey(null)
      accountInfo.value = null            
      return 'OK';
    })
  }

  // 管理画面からのアカウント強制ログインAPI
  const loginKobito = async (
    args: {
      kobitoToken: string 
      __apiOptions?: ApiOptions
    }
  ) => {
    const { loginCompleted } = useLoginStore()
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('loginKobito');

    return await apiCall<'OK'>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/loginkobito";
      const data = {
        kobitoToken: args.kobitoToken,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const loginErrorMsg = `ログインできませんでした`
      const descriptions = {
        ...apiDefaultDescriptions,
        401: loginErrorMsg,
        402: '',
      }

      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }

      // 工事ごとのコンテンツ保管場所のクリア
      await loginCompleted(response.data)

      // トークお知らせURLをセット
      headerStore.talkNotificationUrls = {
        first: response.data.talk_notification_first_request_url,
        next: response.data.talk_notification_next_request_url,
      }
      return 'OK'
    })
  }

  // ログイン完了時のリスナー（前セッションのクリーンアップ等）
  const __loginCompletedListeners: (() => void)[] = []
  const addLoginCompletedListener = (listener: () => void) => {
    __loginCompletedListeners.push(listener)
  }

  const loginCompleted = async(args: { client_key_prefix:string }) => {
    // リスナーを先に呼ぶ（前セッションのクリーンアップ）
    __loginCompletedListeners.forEach(fn => fn())

    const { getSessionId } = useSessionIdStore()
    const contentsStore = useContentsStore()
    // セッションが作成された場合のアクション
    // 工事ごとのコンテンツ保管場所のクリア後に、初期化

    await contentsStore.dispatch.deleteContentsData()
    await contentsStore.dispatch.initializeContentsStorage()

    const sessionId = getSessionId();
    const dispSessId = (sessionId ? sessionId.substring(0, 8) : '');
    loggers.setSessionId(dispSessId)
    loggers.info(`update sessionId: ${dispSessId}`);
    
    if(args.client_key_prefix){
      // セッション開始時にサーバーから返ってくるphotoKeyのPrefixで、初期化
      const { updateClientKeyPrefix } = useItemKeysStore()
      updateClientKeyPrefix(args.client_key_prefix)
    }
  }

  return {
    accountInfo,
    currentAccount,
    updateCurrentAccount,
    checkLoginAccount,
    
    loginAppInfoList,
    setLoginAppInfoList,    
    loginAccount,
    checkTokenActive,
    registerAccountTfaSetting,
    logoutAccount,
    loginKobito,
    loginCompleted,
    addLoginCompletedListener,
  }
},
{
  persist: {
    key: 'KuraemonCloud2_stores.login',
    storage: sessionStorage,
    paths: ['accountInfo'],
  }
})